import React, { useState } from "react";
import { Form, Segment } from "semantic-ui-react";
import countryOptions from "../../Countries";
import SearchLocationInput from "../SearchLocationInput";

const placeLatLng = info => {
  const loc = info?.geometry?.location;
  if (!loc) return null;
  // Fresh PlaceResults expose lat()/lng(); persisted ones round-trip as {lat, lng}.
  return typeof loc.lat === "function"
    ? { lat: loc.lat(), lng: loc.lng() }
    : loc;
};

// The coordinates are place-derived only while they still match the point the
// place blob describes. Derived from the data (not mount-time state) so it
// survives the accordion remount, and a hand-edited coordinate stops matching.
const looksPlaceDerived = address => {
  const loc = placeLatLng(address.info);
  return (
    !!loc &&
    Number(address.latitude) === Number(loc.lat) &&
    Number(address.longitude) === Number(loc.lng)
  );
};

function AddressForm({
  address = {},
  updateAddress,
  touched,
  setTouched,
  showLatLong = false,
}) {
  const [formData, setFormData] = useState({
    id: address.id,
    street1: address.street1 || null,
    street2: address.street2 || "",
    city: address.city || "",
    region: address.region || "",
    postal_code: address.postal_code || "",
    country: address.country || "",
    latitude: address.latitude || null,
    longitude: address.longitude || null,
    info: address.info,
  });

  const propagate = data => {
    if (!touched) {
      setTouched();
    }
    updateAddress(data);
  };

  const handleChange = (_, data) => {
    const formDataCopy = { ...formData };
    let value = data.type === "checkbox" ? data.checked : data.value;
    if (value === "") {
      value = null;
    }
    formDataCopy[data.name] = value;
    setFormData(formDataCopy);
  };

  const handleSelect = (e, { name, value }) => {
    if (name === undefined) return;
    const formDataCopy = { ...formData };
    formDataCopy[name] = value;
    setFormData(formDataCopy);
  };

  const handleBlur = () => {
    propagate(formData);
  };

  const setAddressFields = fields => {
    // Merge only defined keys so a partial update (e.g. a hand-typed street)
    // can't blank the other address fields.
    const newFormData = { ...formData };
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) newFormData[key] = value;
    }
    // When the street is changed by hand away from the place its coordinates
    // describe, drop the now-stale geocode and its blob. A geocode fill (which
    // carries `info`) and coordinates the user typed themselves are left alone.
    const streetChanged =
      "street1" in fields && newFormData.street1 !== formData.street1;
    if (streetChanged && !("info" in fields) && looksPlaceDerived(formData)) {
      newFormData.latitude = null;
      newFormData.longitude = null;
      newFormData.info = null;
    }
    setFormData(newFormData);
    propagate(newFormData);
  };

  return (
    <Segment>
      <Form>
        <SearchLocationInput
          label={"Street 1"}
          inline
          required
          value={formData.street1}
          onChange={setAddressFields}
          address={address}
          country={formData.country}
        />
        <Form.Input
          inline
          name="street2"
          label="Street 2"
          style={{ paddingRight: "1.5em" }}
          width={8}
          value={formData.street2}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        <Form.Group widths="equal">
          <Form.Input
            required
            inline
            name="city"
            label="City"
            value={formData.city}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <Form.Input
            required
            inline
            name="postal_code"
            label="Postal Code"
            value={formData.postal_code}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Input
            required
            inline
            name="region"
            label="Region"
            value={formData.region}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <Form.Select
            selectOnBlur={false}
            inline
            required
            clearable
            label="Country"
            search
            name="country"
            options={countryOptions}
            value={formData.country}
            onChange={handleSelect}
            onBlur={handleBlur}
          />
        </Form.Group>
        {showLatLong && (
          <Form.Group widths="equal">
            <Form.Input
              inline
              name="latitude"
              label="Latitude"
              value={formData.latitude}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <Form.Input
              inline
              name="longitude"
              label="Longitude"
              value={formData.longitude}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Form.Group>
        )}
      </Form>
    </Segment>
  );
}

export default AddressForm;
